import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Badge,
} from '@mui/material';
import {
  Bell,
  ArrowLeft,
  Timer,
} from 'lucide-react';

import { useAppSelector } from '@/store';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { selectActiveTimers } from '@/store/slices/timerSlice';

interface MobileHeaderProps {
  title?: string;
  showBack?: boolean;
  rightAction?: React.ReactNode;
  transparent?: boolean;
}

/**
 * MobileHeader
 * Simplified header for mobile devices
 */
const MobileHeader: React.FC<MobileHeaderProps> = ({
  title,
  showBack = false,
  rightAction,
  transparent = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAppSelector(selectCurrentUser);
  const activeTimers = useAppSelector(selectActiveTimers);

  // Get page title based on route
  const getPageTitle = (): string => {
    if (title) return title;
    
    const path = location.pathname;
    if (path === '/' || path === '/dashboard') return 'Dashboard';
    if (path === '/tasks') return 'My Tasks';
    if (path.startsWith('/tasks/')) return 'Task Details';
    if (path === '/projects') return 'Projects';
    if (path.startsWith('/projects/')) return 'Project Details';
    if (path === '/calendar') return 'Calendar';
    if (path === '/team') return 'Team';
    if (path.startsWith('/team/')) return 'Team Member';
    if (path === '/delegated') return 'Delegated Tasks';
    if (path === '/departments') return 'Departments';
    if (path === '/reviews') return 'Reviews';
    if (path === '/settings') return 'Settings';
    return 'Task Management System';
  };

  // Determine if we should show back button
  const shouldShowBack = (): boolean => {
    if (showBack) return true;
    const path = location.pathname;
    // Show back for detail pages
    return (
      path.startsWith('/tasks/') ||
      path.startsWith('/projects/') ||
      path.startsWith('/team/')
    );
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        display: { xs: 'block', md: 'none' },
        bgcolor: transparent ? 'transparent' : 'background.paper',
        borderBottom: transparent ? 'none' : '1px solid',
        borderColor: 'divider',
      }}
    >
      <Toolbar
        sx={{
          minHeight: 56,
          px: 1.5,
          justifyContent: 'space-between',
        }}
      >
        {/* Left Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {shouldShowBack() ? (
            <IconButton
              onClick={handleBack}
              size="small"
              sx={{ color: 'text.primary' }}
            >
              <ArrowLeft size={22} />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'primary.main',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {user?.firstName?.[0]?.toUpperCase() || 'U'}
              </Avatar>
            </Box>
          )}
          
          <Typography
            variant="h6"
            sx={{
              color: 'text.primary',
              fontWeight: 600,
              fontSize: '1.1rem',
            }}
          >
            {getPageTitle()}
          </Typography>
        </Box>

        {/* Right Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {rightAction || (
            <>
              {/* Active Timers Badge */}
              {activeTimers.length > 0 && (
                <IconButton
                  size="small"
                  onClick={() => navigate('/tasks')}
                  sx={{
                    bgcolor: 'success.50',
                    color: 'success.main',
                    '&:active': { bgcolor: 'success.100' },
                  }}
                >
                  <Badge
                    badgeContent={activeTimers.length}
                    color="success"
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.6rem',
                        minWidth: 14,
                        height: 14,
                        right: -2,
                        top: -2,
                      },
                    }}
                  >
                    <Timer size={18} className="timer-running" />
                  </Badge>
                </IconButton>
              )}

              {/* Notifications */}
              <IconButton size="small" sx={{ color: 'text.secondary' }}>
                <Badge badgeContent={3} color="error" variant="dot">
                  <Bell size={20} />
                </Badge>
              </IconButton>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default MobileHeader;
